import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';

interface EmissionFormulaTooltipProps {
  children: React.ReactNode;
  csvRow: { [key: string]: string };
  serviceType: 'logistics' | 'emergency_services';
  vehicleType?: string;
  totalMiles?: number;
  totalEmissions?: number;
}

interface EmissionFormulaConfig {
  diesel?: {
    /** Emergency-services shape is flat (category -> factor); logistics uses
     *  laden-bucket -> category -> factor. */
    carbon_emission_kg_co2e_per_mile?: Record<string, number | Record<string, number>>;
    idle_burn_rate_litre_per_hour?: Record<string, number>;
    carbon_emission_kg_co2e_per_litre?: number;
  };
  electricity?: {
    carbon_emission_kg_co2e_per_kWh?: number;
  };
  ev?: {
    idle_consumption_rate_kWh_per_hour?: Record<string, number>;
  };
}

export default function EmissionFormulaTooltip({
  children,
  csvRow,
  serviceType,
  vehicleType,
  totalMiles = 0,
  totalEmissions = 0
}: EmissionFormulaTooltipProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [configData, setConfigData] = useState<EmissionFormulaConfig | null>(null);

  // Fetch config data from API
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await authenticatedFetch(API_ENDPOINTS.utilConfig(serviceType));
        if (response.ok) {
          const data = await response.json();
          setConfigData(data);
        }
      } catch (error) {
        console.warn('[EmissionFormulaTooltip] Failed to load config:', error);
      }
    };
    fetchConfig();
  }, [serviceType]);

  // Get correction factors from CSV row
  const avgLadenPct = parseFloat(csvRow.avg_laden_percentage || '0');
  const avgSpeedCorrection = parseFloat(csvRow.avg_speed_correction || '1.0');
  const avgGradientCorrection = parseFloat(csvRow.avg_gradient_correction || '1.0');
  const isEV = vehicleType && (vehicleType.includes('BEV') || vehicleType.includes('ZE') || vehicleType.includes('Electric'));
  const energyConsumption = parseFloat(csvRow.energy_consumption_kWh || '0');
  const activeFuel = parseFloat(csvRow.active_fuel_litre || '0');
  const idleHours = parseFloat(csvRow.idle_hours || csvRow.Idle_Hours || '0');
  const idleBurnRateFromCSV = parseFloat(csvRow.idle_burn_rate_l_per_hour || csvRow.Idle_Burn_Rate_L_per_Hour || '0');
  const activeEmissions = parseFloat(csvRow.active_emission_kg || '0');
  const idleEmissions = parseFloat(csvRow.idle_emission_kg || '0');
  const totalMilesFromCSV = parseFloat(csvRow.total_miles || csvRow.Total_Miles || '0');
  
  // Helper function to determine if a calculation uses Primary Data or Estimated
  const isPrimaryData = (hasTelematics: boolean, hasCorrections: boolean): boolean => {
    return hasTelematics && hasCorrections;
  };
  
  // Determine data source for Active Emissions/Energy
  const hasTelematicsForActive = totalMilesFromCSV > 0;
  const hasSpeedCorrection = avgSpeedCorrection !== 1.0;
  const hasGradientCorrection = avgGradientCorrection !== 1.0;
  const hasLadenPercentage = avgLadenPct > 0 && 
    (typeof csvRow.avg_laden_percentage === 'string' ? 
      csvRow.avg_laden_percentage.toLowerCase() !== 'unavailable' : true);
  const hasCorrectionsForActive = hasSpeedCorrection || hasGradientCorrection || hasLadenPercentage;
  const activeDataIsPrimary = isPrimaryData(hasTelematicsForActive, hasCorrectionsForActive);
  
  // Determine data source for Idle Emissions
  const hasTelematicsForIdle = totalMilesFromCSV > 0; // Same telematics source
  const hasIdleData = idleHours > 0;
  const idleDataIsPrimary = isPrimaryData(hasTelematicsForIdle, hasIdleData);
  
  // Map Emergency Services vehicle types to config categories
  const getESConfigCategory = (vt: string | undefined): 'cars' | 'small_vans' | 'large_vans' | 'HGV' | null => {
    if (!vt) return null;
    const lowerType = vt.toLowerCase();
    if (lowerType.includes('response car') || lowerType.includes('responsecar')) return 'cars';
    if (lowerType.includes('small van') || lowerType.includes('smallvan')) return 'small_vans';
    if (lowerType.includes('large van') || lowerType.includes('largevan')) return 'large_vans';
    if (lowerType.includes('pumping appliance') || lowerType.includes('pumpingappliance') || 
        lowerType.includes('other appliance') || lowerType.includes('otherappliance')) return 'HGV';
    return null;
  };
  
  // Map Logistics vehicle types to config categories
  const getLogisticsConfigCategory = (vt: string | undefined): 'Rigid (>17T)' | 'Articulated (>33T)' | null => {
    if (!vt) return null;
    const lowerType = vt.toLowerCase();
    // Check for articulated first (more specific)
    if (lowerType.includes('articulated') || lowerType.includes('artic')) return 'Articulated (>33T)';
    // Default to Rigid for diesel trucks
    if (lowerType.includes('diesel') || lowerType.includes('truck')) return 'Rigid (>17T)';
    return null;
  };
  
  // Get config values for ES mode based on vehicle type
  const esConfigCategory = serviceType === 'emergency_services' ? getESConfigCategory(vehicleType) : null;
  const configCarbonFactor = (configData?.diesel?.carbon_emission_kg_co2e_per_mile?.[esConfigCategory || ''] as number | undefined) || null;
  const configIdleBurnRate = configData?.diesel?.idle_burn_rate_litre_per_hour?.[esConfigCategory || ''] || null;
  const carbonEmissionPerLitre = configData?.diesel?.carbon_emission_kg_co2e_per_litre || null;
  
  // Get config values for Logistics mode based on vehicle type and laden percentage
  const logisticsConfigCategory = serviceType === 'logistics' ? getLogisticsConfigCategory(vehicleType) : null;
  const carbonFactor0Laden = logisticsConfigCategory
    ? (configData?.diesel?.carbon_emission_kg_co2e_per_mile?.['0% laden'] as Record<string, number> | undefined)?.[logisticsConfigCategory] || null
    : null;
  const carbonFactor100Laden = logisticsConfigCategory
    ? (configData?.diesel?.carbon_emission_kg_co2e_per_mile?.['100% laden'] as Record<string, number> | undefined)?.[logisticsConfigCategory] || null
    : null;
  const logisticsIdleBurnRate = logisticsConfigCategory && configData?.diesel?.idle_burn_rate_litre_per_hour?.[logisticsConfigCategory] || null;
  
  // Get config values for Electric Vehicles
  const electricityEmissionFactor = configData?.electricity?.carbon_emission_kg_co2e_per_kWh || 0.177;
  // Get idle consumption rate for EVs (default to 1.0 kWh/hour if not configured)
  const evIdleConsumptionRate = configData?.ev?.idle_consumption_rate_kWh_per_hour?.[vehicleType || 'truck'] || 
                                 configData?.ev?.idle_consumption_rate_kWh_per_hour?.truck || 1.0;
  
  // Calculate interpolated base factor for Logistics diesel vehicles
  // Base Factor = interpolate between 0% and 100% laden factors based on actual laden percentage
  let calculatedBaseFactor = null;
  if (serviceType === 'logistics' && carbonFactor0Laden !== null && carbonFactor100Laden !== null) {
    if (avgLadenPct > 0 && !isNaN(avgLadenPct)) {
      // Linear interpolation: base = factor_0 + (factor_100 - factor_0) * (laden_pct / 100)
      calculatedBaseFactor = carbonFactor0Laden + (carbonFactor100Laden - carbonFactor0Laden) * (avgLadenPct / 100);
    } else {
      // Use 0% laden factor if laden percentage is not available
      calculatedBaseFactor = carbonFactor0Laden;
    }
  }
  
  // Use config values if available, otherwise fall back to CSV or calculated values
  const idleBurnRate = configIdleBurnRate !== null ? configIdleBurnRate : (idleBurnRateFromCSV > 0 ? idleBurnRateFromCSV : null);
  
  // For Logistics: use calculated base factor from config, otherwise calculate from emissions (fallback)
  const baseFactor = serviceType === 'logistics' && calculatedBaseFactor !== null
    ? calculatedBaseFactor
    : (totalMiles > 0 && avgSpeedCorrection > 0 && avgGradientCorrection > 0 && activeEmissions > 0
        ? activeEmissions / (totalMiles * avgSpeedCorrection * avgGradientCorrection)
        : null);
  
  // For ES mode: use config carbon factor if available, otherwise calculate from data
  const carbonFactor = serviceType === 'emergency_services' 
    ? (configCarbonFactor !== null 
        ? configCarbonFactor 
        : (totalMiles > 0 && avgSpeedCorrection > 0 && activeEmissions > 0
            ? activeEmissions / (totalMiles * avgSpeedCorrection)
            : null))
    : null;

  // For ES mode, check if we have any relevant data
  if (serviceType === 'emergency_services') {
    const hasESData = avgSpeedCorrection !== 1.0 || activeFuel > 0 || totalMiles > 0 || idleHours > 0;
    if (!hasESData) {
      return <>{children}</>;
    }
  } else {
    // For Logistics mode, check correction data
    const hasCorrectionData = avgSpeedCorrection !== 1.0 || avgGradientCorrection !== 1.0 || avgLadenPct > 0;
    if (!hasCorrectionData && !isEV && activeFuel === 0) {
      // No correction data available, show simple tooltip
      return <>{children}</>;
    }
  }

  const handleMouseEnter = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top
      });
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
    setTooltipPosition(null);
  };

  return (
    <>
      <div
        ref={containerRef}
        className="inline-flex items-center gap-1 cursor-help"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
        <HelpCircle className="w-3 h-3 text-muted-foreground hover:text-warning transition-colors" />
      </div>
      {showTooltip && tooltipPosition && createPortal(
        <div
          className="fixed px-4 py-3 bg-background text-white text-xs rounded-lg shadow-xl pointer-events-none z-float border border-border"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y - 8}px`,
            transform: 'translate(-50%, -100%)',
            maxWidth: '500px',
            minWidth: '400px'
          }}
        >
          <div className="font-semibold mb-2 text-warning">Emission Calculation Formula</div>
          
          {serviceType === 'emergency_services' ? (
            <div className="space-y-2 text-foreground">
              <div className="font-medium text-white mb-1">For Emergency Services Vehicles:</div>
              <div className="pl-2 border-l-2 border-border space-y-1.5">
                <div className="text-xs font-mono text-foreground space-y-0.5">
                  <div>Emissions = <span className="text-ring">Active</span> + <span className="text-warning">Idle</span></div>
                  <div className="pl-4"><span className="text-ring">Active</span> = Σ(Segment Distance × Carbon Factor × Segment Speed Correction)</div>
                  <div className="pl-4"><span className="text-warning">Idle</span> = Idle Hours × Idle Burn Rate × Carbon Factor per Litre</div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Speed correction is applied per segment/trip (varies by speed). The average shown is weighted by distance.
                </div>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="font-medium text-white">Base Values:</div>
                  {carbonFactor !== null ? (
                    <div className="pl-2">• <span className="text-ring">Carbon Factor</span>: <span className="text-ring">{carbonFactor.toFixed(3)}</span> kg CO2e/mile</div>
                  ) : (
                    <div className="pl-2 text-muted-foreground">• Carbon Factor: Not available</div>
                  )}
                  {idleBurnRate !== null && idleBurnRate > 0 ? (
                    <div className="pl-2">• <span className="text-warning">Idle Burn Rate</span>: <span className="text-warning">{idleBurnRate.toFixed(2)}</span> L/hour</div>
                  ) : (
                    <div className="pl-2 text-muted-foreground">• Idle Burn Rate: Not available</div>
                  )}
                  {carbonEmissionPerLitre !== null ? (
                    <div className="pl-2">• <span className="text-warning">Carbon Factor per Litre</span>: <span className="text-warning">{carbonEmissionPerLitre.toFixed(5)}</span> kg CO2e/L</div>
                  ) : (
                    <div className="pl-2 text-muted-foreground">• Carbon Factor per Litre: Not available</div>
                  )}
                </div>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="font-medium text-white">Applied Corrections:</div>
                  {avgSpeedCorrection !== 1.0 ? (
                    <div className="pl-2">• Speed: {avgSpeedCorrection.toFixed(3)}x {avgSpeedCorrection > 1 ? '(inefficient speed)' : '(efficient speed)'}</div>
                  ) : (
                    <div className="pl-2 text-muted-foreground">• Speed: 1.000x (no correction)</div>
                  )}
                </div>
                {totalMiles > 0 && activeFuel > 0 && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <div className="font-medium text-white mb-1">Input Data:</div>
                        <div className="space-y-0.5 text-foreground">
                          <div><span className="text-ring">Distance</span>: <span className="text-ring">{totalMiles.toFixed(1)}</span> miles</div>
                          {idleHours > 0 && (
                            <div><span className="text-warning">Idle Time</span>: <span className="text-warning">{idleHours.toFixed(1)}</span> hours</div>
                          )}
                          <div>Fuel: {activeFuel.toFixed(1)} L</div>
                        </div>
                      </div>
                      <div>
                        <div className="font-medium text-white mb-1">Calculation Breakdown:</div>
                        <div className="space-y-1 text-foreground">
                          {activeEmissions > 0 && (
                            <div>
                              <div className="flex items-center gap-2">
                                <span><span className="text-ring">Active</span>: <span className="text-ring">{activeEmissions.toFixed(1)}</span> kg CO2e</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  activeDataIsPrimary 
                                    ? 'bg-success/20 text-success border border-success/50' 
                                    : 'bg-warning/20 text-warning border border-warning/50'
                                }`}>
                                  {activeDataIsPrimary ? '✅ Primary' : '⚠️ Estimated'}
                                </span>
                              </div>
                              <div className="text-muted-foreground text-xs mt-0.5">
                                (Sum of segments × <span className="text-ring">{carbonFactor !== null ? carbonFactor.toFixed(3) : 'CF'}</span> × speed corr.)
                              </div>
                            </div>
                          )}
                          {idleHours > 0 && idleBurnRate !== null && carbonEmissionPerLitre !== null && (
                            <div>
                              <div className="flex items-center gap-2">
                                <span><span className="text-warning">Idle</span>: <span className="text-warning">{(idleHours * idleBurnRate * carbonEmissionPerLitre).toFixed(1)}</span> kg CO2e</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  idleDataIsPrimary 
                                    ? 'bg-success/20 text-success border border-success/50' 
                                    : 'bg-warning/20 text-warning border border-warning/50'
                                }`}>
                                  {idleDataIsPrimary ? '✅ Primary' : '⚠️ Estimated'}
                                </span>
                              </div>
                              <div className="text-muted-foreground text-xs mt-0.5">
                                <span className="text-warning">{idleHours.toFixed(1)}</span> × <span className="text-warning">{idleBurnRate.toFixed(2)}</span> × <span className="text-warning">{carbonEmissionPerLitre.toFixed(5)}</span>
                              </div>
                              {idleEmissions > 0 && Math.abs(idleEmissions - (idleHours * idleBurnRate * carbonEmissionPerLitre)) > 0.1 && (
                                <div className="text-muted-foreground text-xs mt-0.5 italic">
                                  CSV: {idleEmissions.toFixed(1)} (rounded hours, ≥2min filter)
                                </div>
                              )}
                            </div>
                          )}
                          <div className="font-medium text-white mt-1 pt-1 border-t border-border">
                            Total: {totalEmissions.toFixed(1)} kg CO2e
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : isEV ? (
            <div className="space-y-2 text-foreground">
              <div className="font-medium text-white mb-1">For Electric Vehicles:</div>
              <div className="pl-2 border-l-2 border-border space-y-1.5">
                <div className="text-xs font-mono text-foreground space-y-0.5">
                  <div>Emissions = <span className="text-ring">Active</span> + <span className="text-warning">Idle</span></div>
                  <div className="pl-4"><span className="text-ring">Active</span> = Σ(Segment Distance × Base × Laden × Speed × Gradient) × Emission Factor</div>
                  <div className="pl-4"><span className="text-warning">Idle</span> = Idle Hours × Idle Consumption Rate × Emission Factor</div>
                  <div className="pl-4"><span className="text-success">Emission Factor</span> = {electricityEmissionFactor.toFixed(3)} kg CO2e/kWh (UK grid average)</div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Base energy consumption is interpolated from 0% to 100% laden. Speed and gradient corrections are applied per segment (vary by speed/gradient). Idle power consumption is included when vehicle is stationary but powered on.
                </div>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="font-medium text-white">Base Values:</div>
                  <div className="pl-2">• <span className="text-success">Emission Factor</span>: <span className="text-success">{electricityEmissionFactor.toFixed(3)}</span> kg CO2e/kWh</div>
                  {idleHours > 0 && (
                    <div className="pl-2">• <span className="text-warning">Idle Consumption Rate</span>: <span className="text-warning">{evIdleConsumptionRate.toFixed(2)}</span> kWh/hour</div>
                  )}
                </div>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="font-medium text-white">Applied Corrections:</div>
                  {avgLadenPct > 0 ? (
                    <div className="pl-2">• <span className="text-success">Laden</span>: <span className="text-success">{avgLadenPct.toFixed(1)}</span>% (affects base energy consumption)</div>
                  ) : (
                    <div className="pl-2 text-muted-foreground">• Laden: Not available (using 0% laden factor)</div>
                  )}
                  {avgSpeedCorrection !== 1.0 ? (
                    <div className="pl-2">• <span className="text-success">Speed</span>: <span className="text-success">{avgSpeedCorrection.toFixed(3)}</span>x {avgSpeedCorrection > 1 ? '(inefficient speed)' : '(efficient speed)'}</div>
                  ) : (
                    <div className="pl-2 text-muted-foreground">• Speed: 1.000x (no correction)</div>
                  )}
                  {avgGradientCorrection !== 1.0 ? (
                    <div className="pl-2">• <span className="text-success">Gradient</span>: <span className="text-success">{avgGradientCorrection.toFixed(3)}</span>x {avgGradientCorrection > 1 ? '(uphill)' : '(downhill)'}</div>
                  ) : (
                    <div className="pl-2 text-muted-foreground">• Gradient: 1.000x (flat road)</div>
                  )}
                </div>
                {totalMiles > 0 && energyConsumption > 0 && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <div className="font-medium text-white mb-1">Input Data:</div>
                        <div className="space-y-0.5 text-foreground">
                          <div><span className="text-ring">Distance</span>: <span className="text-ring">{totalMiles.toFixed(1)}</span> miles</div>
                          {idleHours > 0 && (
                            <div><span className="text-warning">Idle Time</span>: <span className="text-warning">{idleHours.toFixed(1)}</span> hours</div>
                          )}
                          <div><span className="text-success">Energy</span>: <span className="text-success">{energyConsumption.toFixed(1)}</span> kWh</div>
                        </div>
                      </div>
                      <div>
                        <div className="font-medium text-white mb-1">Calculation Breakdown:</div>
                        <div className="space-y-1 text-foreground">
                          {activeEmissions > 0 && (
                            <div>
                              <div className="flex items-center gap-2">
                                <span><span className="text-ring">Active</span>: <span className="text-ring">{activeEmissions.toFixed(1)}</span> kg CO2e</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  activeDataIsPrimary 
                                    ? 'bg-success/20 text-success border border-success/50' 
                                    : 'bg-warning/20 text-warning border border-warning/50'
                                }`}>
                                  {activeDataIsPrimary ? '✅ Primary' : '⚠️ Estimated'}
                                </span>
                              </div>
                              <div className="text-muted-foreground text-xs mt-0.5">
                                (Sum of segments × base × laden × speed × gradient corr. × {electricityEmissionFactor.toFixed(3)})
                              </div>
                            </div>
                          )}
                          {idleHours > 0 && (
                            <div>
                              <div className="flex items-center gap-2">
                                <span><span className="text-warning">Idle</span>: <span className="text-warning">{(idleHours * evIdleConsumptionRate * electricityEmissionFactor).toFixed(1)}</span> kg CO2e</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  idleDataIsPrimary 
                                    ? 'bg-success/20 text-success border border-success/50' 
                                    : 'bg-warning/20 text-warning border border-warning/50'
                                }`}>
                                  {idleDataIsPrimary ? '✅ Primary' : '⚠️ Estimated'}
                                </span>
                              </div>
                              <div className="text-muted-foreground text-xs mt-0.5">
                                <span className="text-warning">{idleHours.toFixed(1)}</span> × <span className="text-warning">{evIdleConsumptionRate.toFixed(2)}</span> × <span className="text-warning">{electricityEmissionFactor.toFixed(3)}</span>
                              </div>
                              {idleEmissions > 0 && Math.abs(idleEmissions - (idleHours * evIdleConsumptionRate * electricityEmissionFactor)) > 0.1 && (
                                <div className="text-muted-foreground text-xs mt-0.5 italic">
                                  CSV: {idleEmissions.toFixed(1)} (rounded hours, ≥2min filter)
                                </div>
                              )}
                            </div>
                          )}
                          <div className="font-medium text-white mt-1 pt-1 border-t border-border">
                            Total: {totalEmissions.toFixed(1)} kg CO2e
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-2 text-foreground">
              <div className="font-medium text-white mb-1">For Diesel Vehicles:</div>
              <div className="pl-2 border-l-2 border-border space-y-1.5">
                <div className="text-xs font-mono text-foreground space-y-0.5">
                  <div>Emissions = <span className="text-ring">Active</span> + <span className="text-warning">Idle</span></div>
                  <div className="pl-4"><span className="text-ring">Active</span> = Σ(Segment Distance × Base Factor × Segment Speed × Segment Gradient)</div>
                  <div className="pl-4"><span className="text-warning">Idle</span> = Idle Hours × Idle Burn Rate × Carbon Factor per Litre</div>
                </div>
                <div className="text-xs text-muted-foreground">
                  Base Factor = interpolated from 0% to 100% laden based on actual laden %. Speed and gradient corrections are applied per segment (vary by speed/gradient).
                </div>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="font-medium text-white">Base Values:</div>
                  {baseFactor !== null ? (
                    <div className="pl-2">• <span className="text-ring">Base Factor</span>: <span className="text-ring">{baseFactor.toFixed(3)}</span> kg CO2e/mile</div>
                  ) : (
                    <div className="pl-2 text-muted-foreground">• Base Factor: Not available</div>
                  )}
                  {serviceType === 'logistics' && logisticsIdleBurnRate !== null && (
                    <div className="pl-2">• <span className="text-warning">Idle Burn Rate</span>: <span className="text-warning">{logisticsIdleBurnRate.toFixed(2)}</span> L/hour</div>
                  )}
                  {serviceType === 'logistics' && carbonEmissionPerLitre !== null && (
                    <div className="pl-2">• <span className="text-warning">Carbon Factor per Litre</span>: <span className="text-warning">{carbonEmissionPerLitre.toFixed(5)}</span> kg CO2e/L</div>
                  )}
                </div>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="font-medium text-white">Applied Corrections:</div>
                  {avgLadenPct > 0 ? (
                    <div className="pl-2">• Laden: {avgLadenPct.toFixed(1)}% (affects base carbon factor)</div>
                  ) : (
                    <div className="pl-2 text-muted-foreground">• Laden: Not available (using 0% laden factor)</div>
                  )}
                  {avgSpeedCorrection !== 1.0 ? (
                    <div className="pl-2">• Speed: {avgSpeedCorrection.toFixed(3)}x {avgSpeedCorrection > 1 ? '(inefficient speed)' : '(efficient speed)'}</div>
                  ) : (
                    <div className="pl-2 text-muted-foreground">• Speed: 1.000x (no correction)</div>
                  )}
                  {avgGradientCorrection !== 1.0 ? (
                    <div className="pl-2">• Gradient: {avgGradientCorrection.toFixed(3)}x {avgGradientCorrection > 1 ? '(uphill)' : '(downhill)'}</div>
                  ) : (
                    <div className="pl-2 text-muted-foreground">• Gradient: 1.000x (flat road)</div>
                  )}
                </div>
                {totalMiles > 0 && activeFuel > 0 && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <div className="font-medium text-white mb-1">Input Data:</div>
                        <div className="space-y-0.5 text-foreground">
                          <div><span className="text-ring">Distance</span>: <span className="text-ring">{totalMiles.toFixed(1)}</span> miles</div>
                          {idleHours > 0 && (
                            <div><span className="text-warning">Idle Time</span>: <span className="text-warning">{idleHours.toFixed(1)}</span> hours</div>
                          )}
                          <div>Fuel: {activeFuel.toFixed(1)} L</div>
                        </div>
                      </div>
                      <div>
                        <div className="font-medium text-white mb-1">Calculation Breakdown:</div>
                        <div className="space-y-1 text-foreground">
                          {activeEmissions > 0 && (
                            <div>
                              <div className="flex items-center gap-2">
                                <span><span className="text-ring">Active</span>: <span className="text-ring">{activeEmissions.toFixed(1)}</span> kg CO2e</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  activeDataIsPrimary 
                                    ? 'bg-success/20 text-success border border-success/50' 
                                    : 'bg-warning/20 text-warning border border-warning/50'
                                }`}>
                                  {activeDataIsPrimary ? '✅ Primary' : '⚠️ Estimated'}
                                </span>
                              </div>
                              <div className="text-muted-foreground text-xs mt-0.5">
                                (Sum of segments × <span className="text-ring">{baseFactor !== null ? baseFactor.toFixed(3) : 'BF'}</span> × speed × gradient corr.)
                              </div>
                            </div>
                          )}
                          {idleHours > 0 && logisticsIdleBurnRate !== null && carbonEmissionPerLitre !== null && (
                            <div>
                              <div className="flex items-center gap-2">
                                <span><span className="text-warning">Idle</span>: <span className="text-warning">{(idleHours * logisticsIdleBurnRate * carbonEmissionPerLitre).toFixed(1)}</span> kg CO2e</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  idleDataIsPrimary 
                                    ? 'bg-success/20 text-success border border-success/50' 
                                    : 'bg-warning/20 text-warning border border-warning/50'
                                }`}>
                                  {idleDataIsPrimary ? '✅ Primary' : '⚠️ Estimated'}
                                </span>
                              </div>
                              <div className="text-muted-foreground text-xs mt-0.5">
                                <span className="text-warning">{idleHours.toFixed(1)}</span> × <span className="text-warning">{logisticsIdleBurnRate.toFixed(2)}</span> × <span className="text-warning">{carbonEmissionPerLitre.toFixed(5)}</span>
                              </div>
                              {idleEmissions > 0 && Math.abs(idleEmissions - (idleHours * logisticsIdleBurnRate * carbonEmissionPerLitre)) > 0.1 && (
                                <div className="text-muted-foreground text-xs mt-0.5 italic">
                                  CSV: {idleEmissions.toFixed(1)} (rounded hours, ≥2min filter)
                                </div>
                              )}
                            </div>
                          )}
                          <div className="font-medium text-white mt-1 pt-1 border-t border-border">
                            Total: {totalEmissions.toFixed(1)} kg CO2e
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-2 pt-2 border-t border-border text-xs text-muted-foreground">
            <div>Methodology: GLEC Framework aligned</div>
            {serviceType === 'emergency_services' ? (
              <div>Correction factors: EMFAC2021 (speed)</div>
            ) : (
              <div>Correction factors: EMFAC2021 (speed), Research-based (gradient)</div>
            )}
          </div>

          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-background"></div>
        </div>,
        document.body
      )}
    </>
  );
}

